import { getImageData, type IGatsbyImageData } from 'gatsby-plugin-image';

import { isPlainObject, visitObjects } from '../helpers/helpers';

interface ImageAsset {
  url: string;
  filename: string;
  content_type: string;
  gatsbyImageData?: IGatsbyImageData;
  dimension?: { width?: number | null; height?: number | null } | null;
}

const IMAGE_QUALITY = 90;
const IMAGE_BREAKPOINTS = [375, 768, 1280, 1920];
const DEFAULT_IMAGE_WIDTH = 1600;
const DEFAULT_IMAGE_HEIGHT = 900;

const isImageAsset = (value: unknown): value is ImageAsset =>
  isPlainObject(value) &&
  typeof value.url === 'string' &&
  typeof value.filename === 'string' &&
  typeof value.content_type === 'string' &&
  value.content_type.startsWith('image/');

const createImageData = (image: ImageAsset): IGatsbyImageData | undefined => {
  const width = image.dimension?.width || DEFAULT_IMAGE_WIDTH;
  const height = image.dimension?.height || DEFAULT_IMAGE_HEIGHT;

  try {
    return getImageData({
      baseUrl: image.url,
      sourceWidth: width,
      sourceHeight: height,
      width,
      layout: 'constrained',
      formats: ['auto'],
      breakpoints: IMAGE_BREAKPOINTS,
      pluginName: 'live-preview',
      urlBuilder: ({ baseUrl, width: requestedWidth }) =>
        `${baseUrl}?width=${requestedWidth}&quality=${IMAGE_QUALITY}`,
    });
  } catch {
    return undefined;
  }
};

export const addImageData = <T>(data: T): T => {
  visitObjects(data, (object) => {
    if (!isImageAsset(object)) return undefined;

    object.gatsbyImageData ??= createImageData(object);

    return 'skipChildren';
  });

  return data;
};
